import { useRef, useState, type ReactNode } from 'react'
import { Pencil } from 'lucide-react'
import { Err } from '@/components/Err'
import { cn } from '@/lib/utils'
import { digits } from '@/ui/digits'

/**
 * 상세 페이지의 값을 그 자리에서 고친다 — 값을 누르면 같은 자리가 칸이 되고, Enter 나 바깥을 누르면 보내고 Esc 는 되돌린다.
 * 고치는 창을 따로 열면 사람은 그 값이 어디서 바뀌는지 한 번 더 찾아야 한다.
 *
 * 보내는 동안 칸은 잠기고, 거절되면 칸이 열린 채 그 아래에 원인 한 줄이 선다 — 친 글자를 잃지 않고 고쳐 다시 보낸다.
 * 고치지 않은 값은 보내지 않는다. `required` 면 비운 칸은 보내지 않고 되돌린다(이름처럼 비울 수 없는 값).
 * `numeric` 이면 `digits` 가 거른다 — 빈 칸이 뜻을 갖는 자리(`자유`)라 `type="number"` 를 쓰지 않는다.
 * `editable` 이 거짓이면 값만 선다 — 고칠 수 없는 사람에게 연필을 보이지 않는다.
 */
export function InlineEdit({
  label,
  value,
  display,
  onSave,
  placeholder = '',
  suffix = '',
  numeric = false,
  required = false,
  editable = true,
  maxLength = 200,
  className,
}: {
  /** 열린 칸의 접근성 이름. */
  label: string
  /** 칸이 열릴 때의 글자. */
  value: string
  /** 닫힌 모양. */
  display: ReactNode
  onSave: (next: string) => Promise<unknown>
  placeholder?: string
  /** 칸 뒤에 붙는 단위(대·코어·GB). */
  suffix?: string
  numeric?: boolean
  required?: boolean
  editable?: boolean
  maxLength?: number
  className?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  // Enter 로 보낸 뒤 칸이 사라지며 나는 blur 가 한 번 더 보내지 않게.
  const settling = useRef(false)

  if (!editable) return <div className={className}>{display}</div>

  const open = () => {
    setDraft(value)
    setError(null)
    setEditing(true)
  }
  const cancel = () => {
    settling.current = true
    setEditing(false)
    setError(null)
  }
  const commit = async () => {
    if (settling.current || busy) return
    const next = numeric ? draft : draft.trim()
    if (next === value || (required && next === '')) {
      cancel()
      return
    }
    settling.current = true
    setBusy(true)
    setError(null)
    try {
      await onSave(next)
      setEditing(false)
    } catch (err) {
      setError(err)
      settling.current = false
    }
    setBusy(false)
  }

  if (!editing) {
    return (
      <div className={className}>
        {/* 단추의 이름은 값 그대로다 — 제목 안에 서면 그 제목의 이름이 값으로 남아야 한다. 연필은 꾸밈이다. */}
        <button
          type="button"
          onClick={() => {
            settling.current = false
            open()
          }}
          className="group -mx-1 inline-flex max-w-full min-w-0 items-center gap-1.5 rounded px-1 text-left hover:bg-surface-2 focus-visible:bg-surface-2"
        >
          <span className="min-w-0">{display}</span>
          <Pencil className="size-3.5 flex-none text-muted-foreground opacity-60 group-hover:opacity-100" aria-hidden />
        </button>
      </div>
    )
  }

  return (
    <div className={cn('grid gap-1', className)}>
      <div className="flex min-w-0 items-center gap-1.5">
        <input
          aria-label={label}
          autoFocus
          value={draft}
          placeholder={placeholder}
          maxLength={maxLength}
          inputMode={numeric ? 'numeric' : undefined}
          autoComplete="off"
          disabled={busy}
          aria-invalid={error ? true : undefined}
          onChange={(e) => {
            if (!numeric) return setDraft(e.target.value)
            const next = digits(e.target.value)
            if (next !== null) setDraft(next)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void commit()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              e.stopPropagation()
              cancel()
            }
          }}
          onBlur={() => void commit()}
          className="h-8 w-full min-w-0 rounded-md border border-input bg-background px-2 text-base outline-none focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring/50 aria-invalid:border-destructive sm:text-[13px]"
        />
        {suffix ? <span className="flex-none text-[12px] text-muted-foreground">{suffix}</span> : null}
      </div>
      <Err error={error} />
    </div>
  )
}
